import { Component, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { CustomerSearchResult } from '../../../../../models/customer';
import { dateStringFromISO8601, getISO8601DateStringFromMoment, propExists } from '../../../../../utils';
import { AccountService } from '../../../../../services/account/account.service';
import { CustomerService } from '../../../../../services/customer/customer.service';
import * as moment from 'moment';
import { OrganizationConfigService } from '../../../../../services/organization-config/organization-config.service';


@Component({
  selector: 'app-select-customer-modal',
  templateUrl: './select-customer-modal.component.html',
  styleUrls: ['./select-customer-modal.component.css']
})
export class SelectCustomerModalComponent implements OnInit {

  @Input() excludeCustomerIds: number[] = [];
  @Input() submitCallback: (customer: CustomerSearchResult) => undefined;

  public loadingCustomers = false;
  public search = '';
  public searchChangedSubject = new Subject<null>();
  public customerList: CustomerSearchResult[] | null;

  public selectedCustomer: CustomerSearchResult = null;

  public serverError: number = null;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public readonly dateStringFromISO8601 = dateStringFromISO8601;

  // The number of customer list requests. Used to ensure that only the most
  // recent request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private customerListRequestCount = 0;

  private recentActivityFilter: string | undefined = undefined;

  constructor(
    public accountService: AccountService,
    public activeModal: NgbActiveModal,
    private customerService: CustomerService,
    private orgConfigService: OrganizationConfigService,
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    // Handle changes to the customer filters by reloading the customers,
    // subject to debounce.
    this.searchChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadCustomers();
    });
  }

  /**
   * Handle a change in the customer filters. The list of customers will be
   * filtered according to the new values.
   */
  public handleCustomerFilterChange() {
    this.loadingCustomers = true;

    // Reset the selected customer.
    this.selectedCustomer = null;

    const orgConfig = this.orgConfigService.getOrganizationConfig();
    const mustApplyRecentActivityFilter = (
      orgConfig &&
      orgConfig.customer_search_recent_activity_window_months
    );
    if (mustApplyRecentActivityFilter) {
      this.recentActivityFilter = getISO8601DateStringFromMoment(
        moment().subtract(orgConfig.customer_search_recent_activity_window_months, 'months')
      );
    }

    this.searchChangedSubject.next();
  }

  /**
   * Whether a non-zero number of customer results have been loaded.
   */
  public customerResultsPresent(): boolean {
    return (
      this.customerList &&
      (this.customerList.length > 0)
    );
  }

  public filteredCustomerResults(): CustomerSearchResult[] {
    return this.customerList.filter(
      customer => !this.excludeCustomerIds.includes(customer.id)
    );
  }

  /**
   * Handle a click on one of the customer results by displaying the customer's
   * name in the search input.
   */
  public selectCustomer(customer: CustomerSearchResult) {
    this.selectedCustomer = customer;
    this.search = (
      customer.first_name + ' ' + customer.last_name
    );
  }

  /**
   * Whether a valid set of inputs have been selected.
   * Used to determine whether the save button is clickable.
   */
  public validInputSelected() {
    return this.selectedCustomer !== null;
  }

  public handleSave() {
    this.submitCallback(this.selectedCustomer);
    this.activeModal.close();
  }

  public hasSearchInput() {
    return this.search.trim().length >= 3;
  }

  public hasRecentActivityFilter(): boolean {
    return propExists(this.recentActivityFilter);
  }

  public handleExpandSearch() {
    this.recentActivityFilter = undefined;
    this.loadingCustomers = true;
    this.searchChangedSubject.next();
  }

  /**
   * Load the page of customers corresponding to the current page and filter
   * selections.
   */
  private loadCustomers() {
    this.loadingCustomers = true;

    // Increment the request count and save a local copy.
    this.customerListRequestCount++;
    const thisRequestCount = this.customerListRequestCount;

    if (!this.hasSearchInput()) {
      this.customerList = null;
      this.loadingCustomers = false;
      return;
    }

    this.customerService
      .searchCustomers(
        1,
        200,
        {
          search: this.search,
          last_activity_timestamp_min: this.recentActivityFilter,
        },
        true
      )
      .then(response => {
        // Only update the customer list if there has not been a more recent
        // request initiated.
        if (thisRequestCount === this.customerListRequestCount) {
          this.customerList = response.results;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.customerListRequestCount) {
          this.loadingCustomers = false;
        }
      });
  }

}
